/**
 * Custom Design store (Pinia).
 *
 * Holds the user's personal custom themes, the active one, and applies it on
 * top of branding via the custom theme engine. Non-blocking: failures never
 * break the app — they just leave the branding/personal look in place.
 */
import { defineStore } from 'pinia'
import apiClient from '@/api/client'
import { applyCustomTheme, clearCustomTheme, emptyTokens } from '@/modules/appearance/customDesign/customThemeEngine'

export const useCustomThemesStore = defineStore('customThemes', {
  state: () => ({
    themes: [],
    canUse: false,
    bootstrapped: false,
    loading: false,
  }),

  getters: {
    active(state) { return state.themes.find((t) => t.is_active) || null },
    activeId(state) { return (state.themes.find((t) => t.is_active) || {}).id || null },
  },

  actions: {
    async load() {
      this.loading = true
      try {
        const { data } = await apiClient.get('/account/custom-themes/')
        this.themes = data.themes || []
        this.canUse = !!data.can_use
        this.bootstrapped = true
      } catch (e) {
        console.warn('[CustomThemes] load failed:', e?.message)
      } finally {
        this.loading = false
      }
      return this.themes
    },

    /** Apply the active custom theme (if any) to the DOM for the given mode. */
    applyActive(mode = 'light') {
      const a = this.active
      if (a && a.tokens) applyCustomTheme(a.tokens, mode)
      else clearCustomTheme()
    },

    /** Live-preview arbitrary tokens (while editing) without persisting. */
    preview(tokens, mode = 'light') {
      applyCustomTheme(tokens || emptyTokens(), mode)
    },

    async create(name, tokens, baseThemeCode = null) {
      const { data } = await apiClient.post('/account/custom-themes/', {
        name, tokens, base_theme_code: baseThemeCode,
      })
      this.themes = [data, ...this.themes]
      return data
    },

    async update(id, patch) {
      const { data } = await apiClient.patch(`/account/custom-themes/${id}/`, patch)
      const i = this.themes.findIndex((t) => t.id === id)
      if (i >= 0) this.themes[i] = data
      return data
    },

    async remove(id) {
      await apiClient.delete(`/account/custom-themes/${id}/`)
      this.themes = this.themes.filter((t) => t.id !== id)
    },

    async apply(id, mode = 'light') {
      const { data } = await apiClient.post(`/account/custom-themes/${id}/apply/`)
      this.themes = this.themes.map((t) => ({ ...t, is_active: t.id === id }))
      const tokens = (data && data.tokens) || (this.themes.find((t) => t.id === id) || {}).tokens
      if (tokens) applyCustomTheme(tokens, mode)
      return data
    },

    async deactivate(rebrandFn = null) {
      await apiClient.post('/account/custom-themes/deactivate/')
      this.themes = this.themes.map((t) => ({ ...t, is_active: false }))
      clearCustomTheme()
      // Let branding reassert the domain/personal look.
      if (typeof rebrandFn === 'function') rebrandFn()
    },

    async uploadSkin(id, file) {
      const fd = new FormData()
      fd.append('file', file)
      const { data } = await apiClient.post(`/account/custom-themes/${id}/skin/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data.url
    },
  },
})
